import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Label } from '../components/ui/label';
import { Checkbox } from '../components/ui/checkbox';
import { 
  Send, Loader2, CheckCircle2, Calendar, Users, MapPin, 
  Utensils, Music, Camera, Users2, Sparkles
} from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const SERVICE_TYPES = [
  { id: 'catering', label: 'Catering & Food', icon: Utensils },
  { id: 'bar', label: 'Bar & Beverages', icon: Sparkles },
  { id: 'entertainment', label: 'Entertainment & DJ', icon: Music },
  { id: 'photography', label: 'Photography & Video', icon: Camera },
  { id: 'staffing', label: 'Event Staffing', icon: Users2 },
  { id: 'other', label: 'Other Services', icon: Sparkles },
];

export default function HireUsPage() {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    company: '',
    eventDate: '',
    eventTime: '',
    guestCount: '',
    venue: '',
    venueZip: '',
    serviceTypes: [],
    budget: '',
    description: ''
  });

  const handleServiceToggle = (serviceId) => {
    setFormData(prev => ({
      ...prev,
      serviceTypes: prev.serviceTypes.includes(serviceId)
        ? prev.serviceTypes.filter(s => s !== serviceId)
        : [...prev.serviceTypes, serviceId]
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.name || !formData.email || !formData.description) {
      toast.error('Please fill in all required fields');
      return;
    }

    setLoading(true);
    try {
      await axios.post(`${API}/inquiries`, {
        ...formData,
        type: 'hire_us',
        created_at: new Date().toISOString()
      });
      setSubmitted(true);
      toast.success('Your request has been submitted!');
    } catch (error) {
      console.error('Failed to submit inquiry:', error);
      toast.error('Failed to submit. Please try again or contact us directly.');
    } finally {
      setLoading(false);
    }
  };

  if (submitted) {
    return (
      <div className="min-h-screen bg-background pt-20 pb-16 flex items-center justify-center">
        <Card className="max-w-md w-full mx-4 text-center">
          <CardContent className="pt-8 pb-6">
            <div className="w-16 h-16 rounded-full bg-green-100 dark:bg-green-900/30 flex items-center justify-center mx-auto mb-6">
              <CheckCircle2 className="h-8 w-8 text-green-500" />
            </div>
            <h2 className="text-2xl font-bold mb-2">Request Received!</h2>
            <p className="text-muted-foreground mb-6">
              Thank you for reaching out! Our team will review your request and get back to you within 24-48 hours with a custom quote.
            </p>
            <div className="space-y-3">
              <Button className="w-full" onClick={() => navigate('/plan-event')}>
                Explore Our Services
              </Button>
              <Button variant="outline" className="w-full" onClick={() => navigate('/')}>
                Return Home
              </Button>
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background pt-20 pb-16">
      {/* Hero */}
      <div className="bg-gradient-to-b from-slate-900 to-slate-800 text-foreground py-12 -mt-20 pt-32">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h1 className="text-4xl font-bold mb-4">Hire Us</h1>
          <p className="text-lg text-muted-foreground">
            Have a specific vision? Tell us what you need and we'll create a custom proposal for your event.
          </p>
        </div>
      </div>

      <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        <Card>
          <CardHeader>
            <CardTitle>Request a Custom Quote</CardTitle>
            <CardDescription>
              Fill out the form below and our team will get back to you with a personalized proposal.
            </CardDescription>
          </CardHeader>
          <CardContent>
            <form onSubmit={handleSubmit} className="space-y-6">
              {/* Contact Info */}
              <div className="space-y-4">
                <h3 className="font-semibold text-lg">Your Information</h3>
                <div className="grid sm:grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label htmlFor="name">Full Name *</Label>
                    <Input
                      id="name"
                      placeholder="John Smith"
                      value={formData.name}
                      onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                      required
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="email">Email *</Label>
                    <Input
                      id="email"
                      type="email"
                      placeholder="john@example.com"
                      value={formData.email}
                      onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                      required
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="phone">Phone Number</Label>
                    <Input
                      id="phone"
                      type="tel"
                      placeholder="(720) 555-1234"
                      value={formData.phone}
                      onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="company">Company / Organization</Label>
                    <Input
                      id="company"
                      placeholder="Acme Corp"
                      value={formData.company}
                      onChange={(e) => setFormData({ ...formData, company: e.target.value })}
                    />
                  </div>
                </div>
              </div>

              {/* Event Details */}
              <div className="space-y-4 pt-4 border-t">
                <h3 className="font-semibold text-lg flex items-center gap-2">
                  <Calendar className="h-5 w-5 text-indigo-500" />
                  Event Details
                </h3>
                <div className="grid sm:grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label htmlFor="eventDate">Event Date</Label>
                    <Input
                      id="eventDate"
                      type="date"
                      value={formData.eventDate}
                      onChange={(e) => setFormData({ ...formData, eventDate: e.target.value })}
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="eventTime">Event Time</Label>
                    <Input
                      id="eventTime"
                      type="time"
                      value={formData.eventTime}
                      onChange={(e) => setFormData({ ...formData, eventTime: e.target.value })}
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="guestCount" className="flex items-center gap-2">
                      <Users className="h-4 w-4" />
                      Estimated Guest Count
                    </Label>
                    <Input
                      id="guestCount"
                      type="number"
                      placeholder="50"
                      value={formData.guestCount}
                      onChange={(e) => setFormData({ ...formData, guestCount: e.target.value })}
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="budget">Budget Range</Label>
                    <Input
                      id="budget"
                      placeholder="$5,000 - $10,000"
                      value={formData.budget}
                      onChange={(e) => setFormData({ ...formData, budget: e.target.value })}
                    />
                  </div>
                </div>
                <div className="grid sm:grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label htmlFor="venue" className="flex items-center gap-2">
                      <MapPin className="h-4 w-4" />
                      Venue / Location
                    </Label>
                    <Input
                      id="venue"
                      placeholder="Event venue name or address"
                      value={formData.venue}
                      onChange={(e) => setFormData({ ...formData, venue: e.target.value })}
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="venueZip">Venue ZIP Code</Label>
                    <Input
                      id="venueZip"
                      placeholder="80202"
                      value={formData.venueZip}
                      onChange={(e) => setFormData({ ...formData, venueZip: e.target.value })}
                    />
                  </div>
                </div>
              </div>

              {/* Services Needed */}
              <div className="space-y-4 pt-4 border-t">
                <h3 className="font-semibold text-lg">What Services Do You Need?</h3>
                <div className="grid sm:grid-cols-2 md:grid-cols-3 gap-3">
                  {SERVICE_TYPES.map(service => {
                    const Icon = service.icon;
                    const isSelected = formData.serviceTypes.includes(service.id);
                    return (
                      <label
                        key={service.id}
                        className={`flex items-center gap-3 p-3 border rounded-lg cursor-pointer transition-colors ${
                          isSelected ? 'bg-indigo-50 dark:bg-indigo-900/20 border-indigo-500' : 'hover:bg-muted/50'
                        }`}
                      >
                        <Checkbox
                          checked={isSelected}
                          onCheckedChange={() => handleServiceToggle(service.id)}
                        />
                        <Icon className={`h-4 w-4 ${isSelected ? 'text-indigo-500' : 'text-muted-foreground'}`} />
                        <span className="text-sm">{service.label}</span>
                      </label>
                    );
                  })}
                </div>
              </div>

              {/* Description */}
              <div className="space-y-4 pt-4 border-t">
                <h3 className="font-semibold text-lg">Tell Us More</h3>
                <div className="space-y-2">
                  <Label htmlFor="description">Describe Your Event & Needs *</Label>
                  <Textarea
                    id="description"
                    placeholder="Tell us about your event, your vision, any specific requirements, and anything else that will help us create the perfect proposal for you..."
                    className="min-h-[150px]"
                    value={formData.description}
                    onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                    required
                  />
                </div>
              </div>

              {/* Submit */}
              <div className="pt-4">
                <Button
                  type="submit"
                  size="lg"
                  className="w-full bg-gradient-to-r from-indigo-500 to-purple-600 hover:from-indigo-600 hover:to-purple-700"
                  disabled={loading}
                >
                  {loading ? (
                    <>
                      <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                      Submitting...
                    </>
                  ) : (
                    <>
                      <Send className="mr-2 h-4 w-4" />
                      Submit Request
                    </>
                  )}
                </Button>
                <p className="text-xs text-muted-foreground text-center mt-3">
                  We'll respond within 24-48 hours with a custom quote
                </p>
              </div>
            </form>
          </CardContent>
        </Card>

        {/* Quick Contact */}
        <div className="mt-8 text-center text-muted-foreground">
          <p>
            Prefer to talk? Call us at{' '}
            <a href="tel:7209074044" className="text-indigo-500 hover:underline font-medium">(720) 907-4044</a>
            {' '}or email{' '}
            <a href="mailto:team@eventism.com" className="text-indigo-500 hover:underline font-medium">team@eventism.com</a>
          </p>
        </div>
      </div>
    </div>
  );
}
